/* The front door, and there are two of them.
 *
 * ON THE DEMO it lists the four seats on purpose. A prospect walking through this on
 * their own should not have to be told a password, and the seats are the product's
 * shape: the same gym looks different depending on who is looking at it.
 *
 * AGAINST A SERVER it asks for an email and a password, because there is a real gym
 * behind it and real people who work there. This is the only place in the app that
 * knows which kind of source it is talking to. Everywhere else the seam holds. */
import { useState } from 'react';
import { DEMO_ACCOUNTS, DEMO_PASSWORD } from '../data/SeedSource.js';
import { useIsDemo, useSource } from '../data/SourceProvider.js';

export function SignIn({ onIn }: { onIn: () => void }) {
  const demo = useIsDemo();
  return demo ? <DemoSeats onIn={onIn} /> : <RealLogin onIn={onIn} />;
}

function DemoSeats({ onIn }: { onIn: () => void }) {
  const source = useSource();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enter = async (email: string) => {
    setBusy(email); setError(null);
    try { await source.login(email, DEMO_PASSWORD); onIn(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  return (
    <div className="signin">
      <div className="card">
        <h1 className="t-display-xsmall">Acrostic</h1>
        <p className="t-body-medium" style={{ color: 'var(--ink-2)', marginTop: 8 }}>
          Gyms care. Members can&rsquo;t tell.
        </p>
        <p className="t-body-small" style={{ color: 'var(--text-muted)', marginTop: 20 }}>
          A sample gym, with sample people. Pick a seat: the same gym looks different
          from each one.
        </p>
        <div className="seats">
          {DEMO_ACCOUNTS.map((a) => (
            <button
              key={a.email}
              type="button"
              className="seat"
              disabled={busy !== null}
              onClick={() => void enter(a.email)}
            >
              <span className="t-label-large">{a.seat}</span>
              <span className="t-caption" style={{ color: 'var(--text-muted)' }}>
                {busy === a.email ? 'Signing in…' : a.name}
              </span>
            </button>
          ))}
        </div>
        {error && <p className="t-body-small" style={{ color: 'var(--risk)', marginTop: 12 }}>{error}</p>}
      </div>
    </div>
  );
}

function RealLogin({ onIn }: { onIn: () => void }) {
  const source = useSource();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enter(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await source.login(email.trim(), password); onIn(); }
    catch (err) { setError((err as Error).message); setBusy(false); }
  }

  return (
    <div className="signin">
      <form className="card" onSubmit={(e) => void enter(e)}>
        <h1 className="t-display-xsmall">Acrostic</h1>
        <p className="t-body-medium" style={{ color: 'var(--ink-2)', marginTop: 8 }}>
          Gyms care. Members can&rsquo;t tell.
        </p>
        <label className="formfield" style={{ marginTop: 20 }}>
          <span className="t-label-small">Email</span>
          <input className="field" type="email" autoComplete="username" autoFocus
            value={email} onChange={(ev) => setEmail(ev.target.value)} />
        </label>
        <label className="formfield" style={{ marginTop: 12 }}>
          <span className="t-label-small">Password</span>
          <input className="field" type="password" autoComplete="current-password"
            value={password} onChange={(ev) => setPassword(ev.target.value)} />
        </label>
        <button type="submit" className="btn primary t-label-medium"
          style={{ marginTop: 16, width: '100%' }} disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        {/* The server's own words. One message for a wrong email and a wrong
            password, because a login form should not confirm who has an account. */}
        {error && <p className="t-body-small" style={{ color: 'var(--risk)', marginTop: 12 }}>{error}</p>}
      </form>
    </div>
  );
}
